import { normalizePackValue, type VideoPackField } from './videoPack.ts';

/**
 * Visual CMS — the ONE item-field save shape, shared by the detail modal (Slice 2) and the
 * spreadsheet (Slice 3). Both edit the SAME per-item fields (alt / caption / the metadata
 * PACK) through the SAME `media-update` call, which merges metadata. Keeping the patch
 * builders here means the two surfaces can NEVER drift in what they write — and it's the
 * invariant guard: an item-own-field patch, NEVER grouping/assign/cover/order/export.
 */

export interface DetailMediaItem {
  id: string;
  kind: 'image' | 'video';
  url: string | null; // for a video this is the progressive MP4 (rule 18, once encoded)
  poster: string | null;
  alt: string;
  caption: string | null;
  original_filename: string | null;
  metadata: Record<string, unknown> | null;
  width: number | null;
  height: number | null;
}

/** The parent turns this into a `media-update` body + an optimistic local mirror. */
export interface SavePatch {
  apiBody: Record<string, unknown>;
  local: Partial<DetailMediaItem>;
}

/**
 * A metadata PACK field change → a partial `{metadata:{key:next}}` (media-update MERGES, so
 * only the one key is sent) + the fully-merged local bag for the optimistic refresh. An
 * emptied field normalises to null → the server drops the key; the local bag deletes it.
 */
export function metaFieldPatch(item: { metadata: Record<string, unknown> | null }, f: VideoPackField, raw: string): SavePatch {
  const next = normalizePackValue(f.type, raw);
  const merged: Record<string, unknown> = { ...(item.metadata ?? {}) };
  if (next == null) delete merged[f.key];
  else merged[f.key] = next;
  return { apiBody: { metadata: { [f.key]: next } }, local: { metadata: merged } };
}

/** A row-field change (alt / caption). Caption clears to null; alt is a plain trimmed string. */
export function rowFieldPatch(field: 'alt' | 'caption', raw: string): SavePatch {
  const v = raw.trim();
  return { apiBody: { [field]: v }, local: { [field]: field === 'caption' ? v || null : v } as Partial<DetailMediaItem> };
}
